import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { AppConfigService } from '../services/app-config.service';

/**
 * HTTP Interceptor to add authentication headers to API requests
 *
 * Security Features:
 * - Only adds auth token to requests matching platformApiBaseUrl
 * - Only adds Content-Type header when request has a body
 * - Prevents token leakage to external domains or asset requests
 *
 * Automatically adds:
 * - Content-Type: application/json (only for requests with body)
 * - Authorization: Bearer {token} (only for API requests)
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // Skip interception for config loading and other asset requests
  if (req.url.includes('/assets/') || req.url.startsWith('/assets')) {
    return next(req);
  }

  const authService = inject(AuthService);
  const appConfigService = inject(AppConfigService);

  // Only intercept requests to our API and export downloads
  const platformApiBaseUrl = appConfigService.config.api.platformApiBaseUrl;
  const isApiRequest = req.url.startsWith(platformApiBaseUrl) ||
                       req.url.startsWith('/api') ||
                       req.url.startsWith('/exports');

  // Skip interception for non-API requests (external URLs, etc.)
  if (!isApiRequest) {
    return next(req);
  }

  // Build headers object conditionally
  const headers: Record<string, string> = {};

  // Only add Content-Type if the request has a body (POST, PUT, PATCH)
  // Don't add for GET, DELETE, or requests without body
  if (req.body !== null && req.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (authService.accessToken) {
    headers['Authorization'] = `Bearer ${authService.accessToken}`;
  }

  // Only the tenant lookup needs the session ID (via the ID token) for license resolution.
  const isTenantRequest = req.url === `${platformApiBaseUrl}/tenant`;
  if (isTenantRequest && authService.idToken) {
    headers['Renaissance-Session-Id-Token'] = authService.idToken;
  }

  // Clone the request with the headers
  const clonedRequest = req.clone({
    setHeaders: headers
  });

  return next(clonedRequest);
};
